import React, { SFC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, Collapse, IconButton, Link, Tooltip, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import TimelineIcon from '@material-ui/icons/Timeline'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import moment from 'moment'
import {
    ChargingInitSources,
    ChargingSession,
    ChargingSessionState,
    PaymentInterval,
} from '../../shared/types/chargingSession'
import { getLabelForChargerConnectorSocketType, getLabelForChargerConnectorType } from '../../shared/types/charger'
import { consumedKwh } from '../../shared/utils/display'
import { exportFile } from '../../shared/utils/export'
import { config, getToken } from '../../config'
import FlexiPlanDialog from './FlexiPlanDialog'
import CopyableSessionId from './CopyableSessionId'

export type SessionCardVariant = 'user' | 'charger'

const useStyles = makeStyles((theme) => ({
    item: {
        margin: theme.spacing(1, 0, 1, 0),
        padding: theme.spacing(2),
        display: 'flex',
        flexWrap: 'wrap',
        width: '98%',
        boxSizing: 'border-box',
    },
    main: {
        flex: '1 1 0',
        minWidth: 0,
        textAlign: 'left',
    },
    title: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    columns: {
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: theme.spacing(4),
    },
    column: {
        flex: '1 1 220px',
        minWidth: 0,
    },
    nowrap: {
        whiteSpace: 'nowrap',
    },
    pricingLink: {
        display: 'inline',
        cursor: 'pointer',
        font: 'inherit',
        lineHeight: 'inherit',
        verticalAlign: 'baseline',
        padding: 0,
        textAlign: 'left',
        overflowWrap: 'anywhere',
        '& svg': {
            fontSize: '1rem',
            verticalAlign: 'middle',
            marginTop: -2,
        },
    },
    pricingDetail: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius,
        // 3 stlpce x 2 riadky, kazda bunka = popis + hodnota
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        columnGap: theme.spacing(3),
        rowGap: theme.spacing(0.5),
        boxSizing: 'border-box',
    },
    pricingCollapse: {
        flex: '1 0 100%',
        width: '100%',
    },
    pricingCell: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        minWidth: 0,
        textAlign: 'left',
        '& .MuiTypography-caption': {
            fontSize: '0.8rem',
            whiteSpace: 'nowrap',
        },
    },
    pricingValue: {
        whiteSpace: 'nowrap',
        textAlign: 'right',
    },
    actions: {
        flex: '0 0 120px',
        width: 120,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    sessionId: {
        marginTop: 'auto',
        paddingTop: theme.spacing(0.5),
    },
    // ikony flexi a faktury pod sebou
    actionIcons: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    payButton: {
        color: '#c62828',
        borderColor: '#c62828',
        whiteSpace: 'nowrap',
    },
}))

type SessionPaymentStatus = 'free' | 'paid' | 'unpaid' | 'roaming'

const paymentStatusTranslation: Record<SessionPaymentStatus, string> = {
    free: 'users.sessionFree',
    paid: 'users.sessionPaid',
    unpaid: 'users.sessionUnpaid',
    roaming: 'users.sessionRoaming',
}

const stateLabels = {
    [ChargingSessionState.Pending]: 'Pending',
    [ChargingSessionState.InProgress]: 'In Progress',
    [ChargingSessionState.Parking]: 'Parking',
    [ChargingSessionState.Finished]: 'Finished',
    [ChargingSessionState.FailedToSetChargingProfile]: 'Chyba',
}

export const getSessionPrice = (session: ChargingSession): number => {
    if (session.price > 0) {
        return session.price
    }
    return (session.chargingPrice ?? 0) + (session.parkingPrice ?? 0)
}

const getSessionPaymentStatus = (session: ChargingSession): SessionPaymentStatus => {
    if (session.whitelistAuthentication) {
        return 'free'
    }
    if (getSessionPrice(session) === 0) {
        return 'free'
    }
    switch (session.paymentStatus) {
        case 0:
            return 'free'
        case 1:
            return 'paid'
        case 3:
            return 'roaming'
        default:
            // stlpec Paid sa uz nepouziva - rozhoduje len paymentStatus
            return 'unpaid'
    }
}

const initSourceLabels: Record<number, string> = {
    [ChargingInitSources.SEAK]: 'SEAK',
    [ChargingInitSources.HUBJECT]: 'Hubject',
    [ChargingInitSources.VILLAPRO]: 'VillaPro',
    [ChargingInitSources.PLUGNCHARGE]: 'Plug & Charge',
    [ChargingInitSources.QVEND]: 'Q-Vend',
}

const toNumber = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null
    const n = Number(value)
    return isNaN(n) ? null : n
}

interface SessionCardProps {
    session: ChargingSession
    variant?: SessionCardVariant
    // admin moze uhradit session, ktora je nedoplatkom (a user ma kartu)
    canPay?: boolean
    payDisabled?: boolean
    onPay?: (session: ChargingSession) => void
}

const SessionCard: SFC<SessionCardProps> = React.memo(({ session, variant = 'user', canPay, payDisabled, onPay }) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const [flexiOpen, setFlexiOpen] = useState(false)
    const [pricingOpen, setPricingOpen] = useState(false)

    const onExport = () => {
        exportFile(config.proxy, config.api, `ChargingSession/invoice/${session.id}`, getToken(), true)
    }

    const formatDuration = () => {
        const totalSeconds = Math.max(0, Math.floor(moment(session.endedAt).diff(moment(session.startedAt)) / 1000))
        const pad = (n: number) => String(n).padStart(2, '0')
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    }

    const sessionPrice = getSessionPrice(session)
    const paymentStatus = getSessionPaymentStatus(session)
    const hasFlexi = session.flexiChargingSchedule != null || session.flexiChargingScheduleId != null
    const initSource = session.chargingInitSource ?? ChargingInitSources.SEAK

    const getUserTitle = (): string => {
        const email = session.user?.email ?? ''
        const fullName = `${session.user?.firstName ?? ''} ${session.user?.lastName ?? ''}`.trim()
        if (fullName && email) return `${fullName} (${email})`
        if (fullName || email) return fullName || email
        if (initSource === ChargingInitSources.HUBJECT && session.hjIdentificationValue) return session.hjIdentificationValue
        if (initSource === ChargingInitSources.QVEND && session.qVendQid) return session.qVendQid
        if (session.idTag) return `RFID ${session.idTag}`
        return t('users.anonymous')
    }

    const formatTime = (value: any): string => moment(value).format('LT')

    const getConnectorLabel = (): string => {
        const connector = session.chargingConnector
        if (!connector) return `${session.connectorExternalId ?? ''}`
        const type = getLabelForChargerConnectorType(connector.type, t)
        const socket = getLabelForChargerConnectorSocketType(connector.socketType, t)
        return `${session.connectorExternalId ?? connector.externalId} (${socket} ${type})`
    }

    const getCarLabel = (): string | null => {
        const car = session.car
        if (!car) return null
        const name = `${car.brand ?? ''} ${car.model ?? ''}`.trim()
        return car.carLicensePlate ? `${name} (${car.carLicensePlate})` : name
    }

    const getPricingRows = (): Array<[string, string]> => {
        const pc = session.pricingCondition
        if (!pc) return []
        const value = (v: any, suffix = ''): string => {
            const n = toNumber(v)
            return n === null ? '-' : `${n}${suffix}`
        }
        const price = (v: any): string => {
            const n = toNumber(v)
            return n === null ? '-' : `${n.toFixed(2)} €`
        }
        return [
            [t('pricingConditions.priceForKwh'), price(pc.priceForKwh)],
            [t('pricingConditions.limitPerSession'), value(pc.limitPerSession)],
            [t('pricingConditions.maxSpeedOfCharging'), value(pc.maxSpeedOfCharging)],
            [t('pricingConditions.priceForMinuteOfParking'), price(pc.priceForMinuteOfParking)],
            [t('pricingConditions.amountOfFreeParking'), value(pc.amountOfFreeParking)],
            [t('pricingConditions.maximumDurationOfCharging'), value(pc.maximumDurationOfCharging)],
        ]
    }

    const carLabel = getCarLabel()
    const pricingRows = pricingOpen ? getPricingRows() : []

    // posledne slovo nazvu cennika ostava spolu so sipkou, zvysok sa normalne zalamuje
    const pricingName = session.pricingCondition?.name ?? ''
    const pricingNameSplit = pricingName.lastIndexOf(' ') + 1
    const pricingNameHead = pricingName.slice(0, pricingNameSplit)
    const pricingNameTail = pricingName.slice(pricingNameSplit)

    return (
        <Card className={classes.item} elevation={1}>
            <div className={classes.main}>
                <Typography variant="h6" className={classes.title}>
                    {variant === 'charger' ? getUserTitle() : session.location?.name}
                </Typography>

                <div className={classes.columns}>
                    <div className={classes.column}>
                        <Typography component="div" variant="caption">
                            {t('history.time')}: {formatTime(session.startedAt)}
                            {session.startedParkingAt ? ` → ${formatTime(session.startedParkingAt)} (P)` : ''}
                            {session.endedAt ? ` → ${formatTime(session.endedAt)}` : ` → ${t('users.sessionInProgress')}`}
                        </Typography>
                        <Typography component="div" variant="caption">
                            {session.endedAt
                                ? t('users.sessionDuration', { duration: formatDuration(), consumedKwh: consumedKwh(session) })
                                : t('users.sessionInProgress')}
                        </Typography>

                        <Typography component="div" variant="caption">
                            {variant === 'user' ? (
                                <>
                                    {t('history.charger')}: {session.chargerExternalId} | {session.connectorExternalId}
                                </>
                            ) : (
                                <>
                                    {t('history.connector')}: {getConnectorLabel()}
                                </>
                            )}
                        </Typography>
                        <Typography component="div" variant="caption">
                            {t('users.state')}: {stateLabels[session.state]}
                            {initSource !== ChargingInitSources.SEAK ? ` | ${initSourceLabels[initSource] ?? ''}` : ''}
                        </Typography>
                    </div>

                    <div className={classes.column}>
                        {session.idTag && (
                            <Typography component="div" variant="caption">
                                {t('history.rfid')}: {session.idTag}
                            </Typography>
                        )}
                        {carLabel && (
                            <Typography component="div" variant="caption">
                                {t('users.car')}: {carLabel}
                            </Typography>
                        )}
                        <Typography component="div" variant="caption">
                            {t('users.price')}: {sessionPrice.toFixed(2)} € | {t(paymentStatusTranslation[paymentStatus])}
                            {session.paymentInterval === PaymentInterval.MONTHLY ? ` | ${t('users.monthlyBilling')}` : ''}
                            {session.whitelistAuthentication ? ` | ${t('users.whitelist')}` : ''}
                        </Typography>
                        {session.pricingCondition?.name && (
                            <Typography component="div" variant="caption">
                                {t('history.pricing')}:{' '}
                                <Link
                                    component="span"
                                    role="button"
                                    tabIndex={0}
                                    className={classes.pricingLink}
                                    onClick={() => setPricingOpen(!pricingOpen)}
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            setPricingOpen(!pricingOpen)
                                        }
                                    }}
                                >
                                    {pricingNameHead}
                                    <span className={classes.nowrap}>
                                        {pricingNameTail}
                                        {pricingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </span>
                                </Link>
                            </Typography>
                        )}
                    </div>
                </div>

            </div>

            <div className={classes.actions}>
                <div className={classes.actionIcons}>
                    {hasFlexi && (
                        <Tooltip title={t('history.flexiPlan') as string}>
                            <IconButton onClick={() => setFlexiOpen(true)}>
                                <TimelineIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {session.hasInvoice && (
                        <Tooltip title={t('history.invoice') as string}>
                            <IconButton onClick={onExport}>
                                <PictureAsPdfIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
                {canPay && onPay && (
                    <Button
                        size="small"
                        variant="outlined"
                        className={classes.payButton}
                        disabled={payDisabled}
                        onClick={() => onPay(session)}
                    >
                        {t('users.paySession', { amount: sessionPrice.toFixed(2) })}
                    </Button>
                )}
                <CopyableSessionId id={session.id} className={classes.sessionId} />
            </div>

            <Collapse in={pricingOpen} unmountOnExit className={classes.pricingCollapse}>
                <div className={classes.pricingDetail}>
                    {pricingRows.map(([label, value]) => (
                        <div key={label} className={classes.pricingCell}>
                            <Typography variant="caption" color="textSecondary">
                                {label}
                            </Typography>
                            <Typography variant="caption" className={classes.pricingValue}>
                                {value}
                            </Typography>
                        </div>
                    ))}
                </div>
            </Collapse>

            {flexiOpen && <FlexiPlanDialog open={flexiOpen} onClose={() => setFlexiOpen(false)} session={session} />}
        </Card>
    )
})

export default SessionCard
